// frontend/src/services/api.js
import axios from 'axios';

const BASE = process.env.REACT_APP_API_URL || '/api';

const client = axios.create({ baseURL: BASE, withCredentials: true });

// Active department — the single scope control (DepartmentContext sets this).
// The interceptor injects ?department_id=X on GETs; null = "all departments"
// (no filter). The backend DERIVES the program from this department, so there
// is no separate program_id to inject. Explicit params on a call (e.g. the
// Sales pages pass program_id: 2) still win thanks to the merge order below.
let _activeDepartmentId = null;
export function setActiveDepartmentInApi(id) {
  _activeDepartmentId = id ? Number(id) : null;
}

client.interceptors.request.use(cfg => {
  const token = localStorage.getItem('epro_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  const method = (cfg.method || 'get').toLowerCase();
  if (method === 'get') {
    const injected = {};
    if (_activeDepartmentId) injected.department_id = _activeDepartmentId;
    // Explicit params on the call still win (merge order).
    cfg.params = { ...injected, ...(cfg.params || {}) };
  }
  return cfg;
});

client.interceptors.response.use(
  r => r,
  err => {
    const isLoginRequest = err.config?.url?.includes('/auth/login');
    if (err.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem('epro_token');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// Auth
export const login = (email, password) =>
  client.post('/auth/login', { email, password }).then(r => r.data);
export const logout = () =>
  client.post('/auth/logout').then(r => r.data);
export const getMe = () =>
  client.get('/auth/me').then(r => r.data);
export const requestPasswordReset = (email) =>
  client.post('/auth/forgot-password', { email }).then(r => r.data);
export const resetPassword = (email, code, password) =>
  client.post('/auth/reset-password', { email, code, password }).then(r => r.data);

// Users
export const getUsers = (params = {}) =>
  client.get('/users', { params }).then(r => r.data);
export const getUser = id =>
  client.get(`/users/${id}`).then(r => r.data);
export const createUser = data =>
  client.post('/users', data).then(r => r.data);
export const updateUser = (id, data) =>
  client.put(`/users/${id}`, data).then(r => r.data);
export const deleteUser = id =>
  client.delete(`/users/${id}`).then(r => r.data);
export const getHierarchy = () =>
  client.get('/users/hierarchy').then(r => r.data);
export const updateUserManager = (userId, managerId) =>
  client.put(`/users/${userId}/manager`, { manager_id: managerId }).then(r => r.data);
export const updateUserRole = (userId, role) =>
  client.put(`/users/${userId}/role`, { role }).then(r => r.data);

// Reviews
export const getReviews = (params = {}) =>
  client.get('/reviews', { params }).then(r => r.data);
export const getCycles = (params = {}) =>
  client.get('/reviews/cycles', { params }).then(r => r.data);
export const createCycle = data =>
  client.post('/reviews/cycles', data).then(r => r.data);
export const updateCycle = (id, data) =>
  client.put(`/reviews/cycles/${id}`, data).then(r => r.data);
export const deleteCycle = id =>
  client.delete(`/reviews/cycles/${id}`).then(r => r.data);
export const submitReview = data =>
  client.post('/reviews', data).then(r => r.data);
export const deleteReview = id =>
  client.delete(`/reviews/${id}`).then(r => r.data);
export const getDashboardStats = () =>
  client.get('/reviews/dashboard-stats').then(r => r.data);
// Per-cycle eligibility. Marking someone not eligible takes them out of the cycle
// entirely: no review, no KPI scoring, no reminders, and the cycle is skipped by
// annual-bonus averaging.
export const getCycleEligibility = cycleId =>
  client.get('/reviews/eligibility', { params: { cycle_id: cycleId } }).then(r => r.data);
export const markNotEligible = (cycleId, employeeId, reason) =>
  client.post('/reviews/eligibility', { cycle_id: cycleId, employee_id: employeeId, reason }).then(r => r.data);
export const markEligible = exclusionId =>
  client.delete(`/reviews/eligibility/${exclusionId}`).then(r => r.data);

// OKRs
export const getOKRs = (params = {}) =>
  client.get('/okrs', { params }).then(r => r.data);
export const createOKR = data =>
  client.post('/okrs', data).then(r => r.data);
export const updateOKR = (id, data) =>
  client.put(`/okrs/${id}`, data).then(r => r.data);
export const deleteOKR = id =>
  client.delete(`/okrs/${id}`).then(r => r.data);
export const updateKR = (id, data) =>
  client.put(`/okrs/kr/${id}`, data).then(r => r.data);

// Check-ins
export const getCheckins = () =>
  client.get('/checkins').then(r => r.data);
export const createCheckin = data =>
  client.post('/checkins', data).then(r => r.data);
export const updateCheckin = (id, data) =>
  client.put(`/checkins/${id}`, data).then(r => r.data);
export const deleteCheckin = id =>
  client.delete(`/checkins/${id}`).then(r => r.data);

// Feedback
export const getFeedback = (params = {}) =>
  client.get('/feedback', { params }).then(r => r.data);
export const getFeedbackUsers = () =>
  client.get('/feedback/users').then(r => r.data);
export const submitFeedback = data =>
  client.post('/feedback', data).then(r => r.data);
export const getFeedbackSummary = userId =>
  client.get(`/feedback/summary/${userId}`).then(r => r.data);

// Departments
export const getDepartments = () =>
  client.get('/departments').then(r => r.data);
export const createDepartment = data =>
  client.post('/departments', data).then(r => r.data);
export const updateDepartment = (id, data) =>
  client.put(`/departments/${id}`, data).then(r => r.data);
export const deleteDepartment = id =>
  client.delete(`/departments/${id}`).then(r => r.data);

// Sales Team Program — Action Plans
export const getActionPlans = (params = {}) =>
  client.get('/action-plans', { params }).then(r => r.data);
export const getActionPlansTeam = cycleId =>
  client.get('/action-plans/team', { params: { cycle_id: cycleId } }).then(r => r.data);
export const updateActionPlan = (id, data) =>
  client.put(`/action-plans/${id}`, data).then(r => r.data);

// Sales Team Program — Weekly Journal
export const getJournal = (cycleId, employeeId) =>
  client.get('/journal', { params: { cycle_id: cycleId, employee_id: employeeId } }).then(r => r.data);
export const saveJournalEntry = data =>
  client.post('/journal', data).then(r => r.data);
export const updateJournalEntry = (id, data) =>
  client.put(`/journal/${id}`, data).then(r => r.data);
export const deleteJournalEntry = id =>
  client.delete(`/journal/${id}`).then(r => r.data);

// Sales Team Program — Group Alignment Sessions
export const getGASSessions = (cycleId) =>
  client.get('/gas', { params: cycleId ? { cycle_id: cycleId } : {} }).then(r => r.data);
export const getGASSession = id =>
  client.get(`/gas/${id}`).then(r => r.data);
export const createGASSession = data =>
  client.post('/gas', data).then(r => r.data);
export const updateGASSession = (id, data) =>
  client.put(`/gas/${id}`, data).then(r => r.data);
export const deleteGASSession = id =>
  client.delete(`/gas/${id}`).then(r => r.data);

// Performance programs
export const getPrograms = () =>
  client.get('/programs').then(r => r.data);
export const createProgram = data =>
  client.post('/programs', data).then(r => r.data);
export const updateProgram = (id, data) =>
  client.put(`/programs/${id}`, data).then(r => r.data);
export const deleteProgram = id =>
  client.delete(`/programs/${id}`).then(r => r.data);

// Role types
export const getRoleTypes = (params = {}) =>
  client.get('/role-types', { params }).then(r => r.data);
export const createRoleType = data =>
  client.post('/role-types', data).then(r => r.data);
export const updateRoleType = (id, data) =>
  client.put(`/role-types/${id}`, data).then(r => r.data);
export const deleteRoleType = id =>
  client.delete(`/role-types/${id}`).then(r => r.data);

// KPIs
export const getKPITemplates = () =>
  client.get('/kpis/templates').then(r => r.data);
export const createKPITemplate = data =>
  client.post('/kpis/templates', data).then(r => r.data);
export const updateKPITemplate = (id, data) =>
  client.put(`/kpis/templates/${id}`, data).then(r => r.data);
export const deleteKPITemplate = id =>
  client.delete(`/kpis/templates/${id}`).then(r => r.data);
export const getKPIAssignments = (params = {}) =>
  client.get('/kpis/assignments', { params }).then(r => r.data);
export const assignKPI = data =>
  client.post('/kpis/assignments', data).then(r => r.data);
export const updateKPIWeights = (assignmentId, items) =>
  client.put(`/kpis/assignments/${assignmentId}/weight`, items).then(r => r.data);
export const deleteKPIAssignment = id =>
  client.delete(`/kpis/assignments/${id}`).then(r => r.data);
export const submitSelfEvaluation = items =>
  client.post('/kpis/self-evaluate', items).then(r => r.data);
export const submitManagerScores = items =>
  client.post('/kpis/manager-score', items).then(r => r.data);
export const getKPIReviewForm = (employeeId, cycleId) =>
  client.get('/kpis/review-form', { params: { employee_id: employeeId, cycle_id: cycleId } }).then(r => r.data);
export const getBonusPreview = (employeeId, year) =>
  client.get(`/kpis/bonus/${employeeId}`, { params: { year } }).then(r => r.data);
export const generateBonus = data =>
  client.post('/kpis/bonus/generate', data).then(r => r.data);
export const getTeamBonus = year =>
  client.get(`/kpis/bonus/team/${year}`).then(r => r.data);
export const getQuarterlyScores = cycleId =>
  client.get('/kpis/quarterly-scores', { params: { cycle_id: cycleId } }).then(r => r.data);
export const declareChampion = data =>
  client.post('/kpis/champion', data).then(r => r.data);
export const getChampions = year =>
  client.get('/kpis/champions', { params: { year } }).then(r => r.data);
export const bulkAssignKPIs = data =>
  client.post('/kpis/assignments/bulk', data).then(r => r.data);
export const archiveKPISet = data =>
  client.post('/kpis/assignments/archive-set', data).then(r => r.data);
export const reassignKPISet = data =>
  client.post('/kpis/assignments/reassign', data).then(r => r.data);
// Which cycle a target would inherit KPIs from, and how much would come across.
// Pass { cycle_id } for an existing cycle, or { department_id, start_date } for a
// cycle that doesn't exist yet (the create form needs the preview before saving).
export const getCarryForwardSource = (params = {}) =>
  client.get('/kpis/carry-forward/source', { params }).then(r => r.data);
export const carryForwardKPIs = (sourceCycleId, targetCycleId) =>
  client.post('/kpis/carry-forward', {
    source_cycle_id: sourceCycleId, target_cycle_id: targetCycleId,
  }).then(r => r.data);

// Review Sessions
export const getReviewSessions = () =>
  client.get('/sessions').then(r => r.data);
export const createReviewSession = data =>
  client.post('/sessions', data).then(r => r.data);
export const updateReviewSession = (id, data) =>
  client.put(`/sessions/${id}`, data).then(r => r.data);
export const deleteReviewSession = id =>
  client.delete(`/sessions/${id}`).then(r => r.data);

// Setup readiness & diagnosis — powers the guide's live checklist and the
// "why is this empty?" states. Read-only.
export const getSetupReadiness = (params = {}) =>
  client.get('/setup/readiness', { params }).then(r => r.data);
export const diagnoseSetup = (params = {}) =>
  client.get('/setup/diagnose', { params }).then(r => r.data);

// Profile
export const getProfile = userId =>
  client.get(`/profile/${userId}`).then(r => r.data);
export const updateProfile = (userId, data) =>
  client.put(`/profile/${userId}`, data).then(r => r.data);

// Career
export const getCareerLevels = () =>
  client.get('/career/levels').then(r => r.data);
export const getUserCareer = userId =>
  client.get(`/career/${userId}`).then(r => r.data);
export const updateCareer = (userId, data) =>
  client.put(`/career/${userId}`, data).then(r => r.data);
export const addDevPlanItem = data =>
  client.post('/career/devplan', data).then(r => r.data);
export const updateDevPlanItem = (id, data) =>
  client.put(`/career/devplan/${id}`, data).then(r => r.data);
export const deleteDevPlanItem = id =>
  client.delete(`/career/devplan/${id}`).then(r => r.data);
